import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { autoMobileVehicleService } from '../../../../services/autoMobileVehicleService';
import { customerService } from '../../../../services/customerService';
import { ArrowLeft, Car, AlertCircle, Zap } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';
import '../AutoMobilePage.css';

interface FormData {
  customerId: number;
  registrationNumber: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  color: string;
  mileage: number;
  notes: string;
  isActive: boolean;
}

const blank = (): FormData => ({
  customerId: 0, registrationNumber: '', vin: '', make: '', model: '',
  year: new Date().getFullYear(), color: '', mileage: 0, notes: '', isActive: true,
});

const VehicleCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isEdit = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [formData, setFormData] = useState<FormData>(blank());
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    customerService.getCustomers({ pageNumber: 1, pageSize: 500 })
      .then(res => {
        const d = (res.data as any)?.data ?? [];
        setCustomers(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingData(true);
    autoMobileVehicleService.getById(parseInt(id))
      .then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setFormData({
          customerId: d.customerId ?? 0,
          registrationNumber: d.registrationNumber ?? '',
          vin: d.vin ?? '',
          make: d.make ?? '',
          model: d.model ?? '',
          year: d.year ?? new Date().getFullYear(),
          color: d.color ?? '',
          mileage: d.mileage ?? 0,
          notes: d.notes ?? '',
          isActive: d.isActive ?? true,
        });
      })
      .catch(() => setError(t('automobile.vehicle.failedToLoad')))
      .finally(() => setLoadingData(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(p => ({ ...p, [field]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value }));
      setFieldErrors(p => ({ ...p, [field]: undefined }));
    };

  const validate = () => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!formData.customerId) errs.customerId = t('common.required');
    if (!formData.registrationNumber.trim()) errs.registrationNumber = t('common.required');
    if (!formData.make.trim()) errs.make = t('common.required');
    if (!formData.model.trim()) errs.model = t('common.required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true); setError(null);
    const payload = {
      customerId: formData.customerId,
      registrationNumber: formData.registrationNumber.trim(),
      vin: formData.vin.trim() || undefined,
      make: formData.make.trim(),
      model: formData.model.trim(),
      year: formData.year,
      color: formData.color.trim() || undefined,
      mileage: formData.mileage,
      notes: formData.notes.trim() || undefined,
      isActive: formData.isActive,
    };
    try {
      if (isEdit && id) await autoMobileVehicleService.update(parseInt(id), payload);
      else await autoMobileVehicleService.create(payload);
      navigate('/automobile/vehicles');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('automobile.vehicle.failedToSave'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page auto-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="auto-create-page">
        <div className="ims-create-body-page">

          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/automobile/vehicles')}>
                <ArrowLeft size={13} /> {t('automobile.vehicle.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Car size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('automobile.vehicle.editTitle') : t('automobile.vehicle.createTitle')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit ? t('automobile.vehicle.editSubtitle') : t('automobile.vehicle.createSubtitle')}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/automobile/vehicles')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          <div className="ims-create-body ims-create-body-single">
            <div className="ims-panel-center" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Car size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('automobile.vehicle.details')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className={`mi-field${fieldErrors.customerId ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">{t('automobile.vehicle.customer')} <span className="mi-required">*</span></label>
                    <select className="mi-input" value={formData.customerId} onChange={set('customerId')}>
                      <option value={0}>{t('automobile.vehicle.selectCustomer')}</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {fieldErrors.customerId && <span className="mi-err">{fieldErrors.customerId}</span>}
                  </div>

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.registrationNumber ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('automobile.vehicle.registrationNumber')} <span className="mi-required">*</span></label>
                      <input type="text" className="mi-input" value={formData.registrationNumber} onChange={set('registrationNumber')}
                        placeholder="e.g. WP-CAB-1234" maxLength={50} autoFocus />
                      {fieldErrors.registrationNumber && <span className="mi-err">{fieldErrors.registrationNumber}</span>}
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('automobile.vehicle.vin')}</label>
                      <input type="text" className="mi-input mi-input-mono" value={formData.vin} onChange={set('vin')}
                        placeholder="Vehicle Identification Number" maxLength={50} />
                    </div>
                  </div>

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.make ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('automobile.vehicle.make')} <span className="mi-required">*</span></label>
                      <input type="text" className="mi-input" value={formData.make} onChange={set('make')}
                        placeholder="e.g. Toyota" maxLength={100} />
                      {fieldErrors.make && <span className="mi-err">{fieldErrors.make}</span>}
                    </div>
                    <div className={`mi-field${fieldErrors.model ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('automobile.vehicle.model')} <span className="mi-required">*</span></label>
                      <input type="text" className="mi-input" value={formData.model} onChange={set('model')}
                        placeholder="e.g. Corolla" maxLength={100} />
                      {fieldErrors.model && <span className="mi-err">{fieldErrors.model}</span>}
                    </div>
                  </div>

                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">{t('automobile.vehicle.year')}</label>
                      <input type="number" className="mi-input" value={formData.year} onChange={set('year')} min={1950} max={2100} />
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('automobile.vehicle.color')}</label>
                      <input type="text" className="mi-input" value={formData.color} onChange={set('color')} maxLength={50} />
                    </div>
                  </div>

                  <div className="mi-field">
                    <label className="mi-label">{t('automobile.vehicle.mileage')}</label>
                    <input type="number" className="mi-input" value={formData.mileage} onChange={set('mileage')} min={0} />
                  </div>

                  <div className="mi-field">
                    <label className="mi-label">{t('common.notes')}</label>
                    <textarea className="mi-input mi-textarea" value={formData.notes} onChange={set('notes')} maxLength={500} />
                  </div>
                </div>
              </div>

              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Zap size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('common.status')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <label className={`mi-toggle-row${formData.isActive ? ' mi-toggle-on' : ''}`}>
                    <div className="mi-toggle-text">
                      <span className="mi-toggle-label">{t('common.active')}</span>
                      <span className="mi-toggle-sub">{t('automobile.vehicle.activeDesc')}</span>
                    </div>
                    <div className={`mi-switch${formData.isActive ? ' mi-switch-on' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, isActive: !p.isActive }))}>
                      <div className="mi-switch-thumb" />
                    </div>
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default VehicleCreatePage;
